import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Organization} from '@models/organization/organization';
import {MachineService} from '@services/administration/machine/machine.service';
import {NotificationService} from '@services/notification/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {Machine} from '@models/machine/machine';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {User} from '@models/user/user';
import { HttpResponse } from '@angular/common/http';
import {USER_ROLE} from '@constants/user-role.constant';

@Component({
    selector: 'scitis-select-machines-dropdown',
    templateUrl: './select-machines-dropdown.component.html',
    styleUrls: ['./select-machines-dropdown.component.scss'],
    standalone: false
})
export class SelectMachinesDropdownComponent implements OnInit {

  @Input()
  public organizationId: string | null = null;

  @Output()
  selectedMachineIds = new EventEmitter<number[]>();

  public isLoadingOrganizations: boolean = false;
  public organizationsList: Organization[] = [];
  public machinesList: Machine[] = [];
  public isLoadingMachinesOfOrganization: boolean = false;
  public selectedMachinesList: Machine[] = [];

  private CURRENT_PAGE_NAME = 'SelectMachinesComponent';

  constructor(
      private firebaseAuthenticationService: FirebaseAuthenticationService,
      private machineService: MachineService,
      private notificationService: NotificationService,
      private elasticSearchService: ElasticSearchService,
      private translate: TranslateService) {
  }

  ngOnInit() {
    this.getAuthenticatedUserOrganizations();
  }

  public onChangeOrganization() {
    this.machinesList = [];
    this.setMachinesOfOrganization(this.organizationId);
  }

  public onUpdateSelectedMachines() {
    const selectedMachineIds: number[] = [];
    for (const selectedMachine of this.selectedMachinesList) {
      if (selectedMachine.id) {
        selectedMachineIds.push(selectedMachine.id);
      }
    }
    this.selectedMachineIds.emit(selectedMachineIds);
  }

  public onSelectAll(): void {
    for (const machine of this.machinesList) {
      const index = this.selectedMachinesList.findIndex(m => m.id === machine.id);
      if (index === -1) {
        this.selectedMachinesList.push(machine);
      }
    }
    this.selectedMachinesList = [...this.selectedMachinesList];
    this.onUpdateSelectedMachines();
  }

  public onClearAll() {
    this.selectedMachinesList = [];
    this.onUpdateSelectedMachines();
  }

  public compareMachines(machineItem: Machine, selectedMachine: Machine): boolean {
    // more than 1 machine with same type (bindLabel) can exist, so compare function check the id property in order to match the object
    return machineItem.id === selectedMachine.id;
  }

  private getAuthenticatedUserOrganizations() {
    this.firebaseAuthenticationService.getLoggedUserDetails().subscribe((user: User) => {
      const rootOrganizationId = USER_ROLE.SUPERADMIN === user.role ? -1 : user.parentId;
      this.getAllOrganizationsForUser(rootOrganizationId);
    }, (error: HttpResponse<any>) => {
      console.error(`${this.CURRENT_PAGE_NAME}. Error getting current user details.`, JSON.stringify(error));
    });
  }

  private getAllOrganizationsForUser(rootOrganizationId: number): void {
    this.isLoadingOrganizations = true;

    this.elasticSearchService.listAllOrganizations(rootOrganizationId)
    .subscribe({
      next: (result: ElasticSearchResponseEntity) => {
        if (result.result.data) {
          this.organizationsList = [...result.result.data];
          if (this.isInvalidNumericId(result.result.data[0].id)) {
            this.organizationId = result.result.data[0].id;
          }
          this.onChangeOrganization();

          this.isLoadingOrganizations = false;
        }
      },
      error: err => {
        this.isLoadingOrganizations = false;
        const message = this.translate.instant('There was an error loading user available organizations');
        this.notificationService.error(message, err, this.CURRENT_PAGE_NAME, true);
        this.isLoadingOrganizations = false;
      }
    });

  }

  private setMachinesOfOrganization(organizationId: string | null): void {
    if (this.isInvalidNumericId(organizationId)) {
      this.machinesList = [];
      return;
    }

    this.isLoadingMachinesOfOrganization = true;
    this.machineService.listForOrganization(Number(organizationId)).subscribe((success: Machine[]) => {
      this.machinesList = [...success];
      this.isLoadingMachinesOfOrganization = false;
    }, error => {
      this.isLoadingMachinesOfOrganization = false;
      const message = this.translate.instant('Error loading machines list or organization.');
      this.notificationService.error(message, error, this.CURRENT_PAGE_NAME, true);
    });
  }

  private isInvalidNumericId(id: string | null): boolean {
    return !id || isNaN(Number(id));
  }

}
